import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { procurementAssistantApi } from '@/lib/procurementAssistantApi';
import { PROCUREMENT_QUESTIONS, assistantError, assistantOutcomeUncertain, procurementContextKey } from '@/lib/procurementAssistantRequests';
import ProcurementAnswer from '@/components/Procurement/ProcurementAnswer';

export default function ProcurementGMAssistantPage() {
  const { user, authenticated, isAdmin, isDeveloper, isManager } = useUserPermissions();
  const canView = authenticated && (isAdmin || isDeveloper || isManager);
  const key = procurementContextKey(user);
  if (!canView) return <div className="p-6 text-red-300">You do not have access to the Procurement Assistant.</div>;
  if (!key) return <div className="p-6 text-slate-300">Select your unit and refresh your session to open the assistant.</div>;
  return <AssistantWorkspace key={key} user={user} />;
}

function AssistantWorkspace({ user }) {
  const api = useMemo(() => procurementAssistantApi({ company_id: user.company_id,
    unit_id: user.unit_id, user_id: user.id }), [user.company_id, user.unit_id, user.id]);
  const [ready, setReady] = useState(false), [connecting, setConnecting] = useState(true);
  const [question, setQuestion] = useState(''), [messages, setMessages] = useState([]);
  const [conversation, setConversation] = useState(null), [older, setOlder] = useState(null);
  const [recent, setRecent] = useState([]), [recentCursor, setRecentCursor] = useState(null);
  const [busy, setBusy] = useState(null), [loadingRecent, setLoadingRecent] = useState(false);
  const [error, setError] = useState(''), [recentError, setRecentError] = useState('');
  const [uncertain, setUncertain] = useState(false), [conversationLoaded, setConversationLoaded] = useState(true);
  const [ratings, setRatings] = useState({});
  const alive = useRef(true), lock = useRef(false), recentLock = useRef(false), view = useRef(0), serial = useRef(0);
  const controllers = useRef(new Set()), bottomRef = useRef(null);
  const privacy = useRef(0);

  function request() {
    const controller = new AbortController(); controllers.current.add(controller);
    return { signal: controller.signal, done: () => controllers.current.delete(controller) };
  }
  const valid = (epoch) => alive.current && epoch === view.current;
  function clearPrivateData() {
    view.current++; privacy.current++; setMessages([]); setConversation(null); setOlder(null);
    setQuestion(''); setRecent([]); setRecentCursor(null); setRatings({}); setConversationLoaded(true);
  }
  function denyIfNeeded(cause) {
    if (cause?.response?.status === 401 || cause?.code === 'AI_CLIENT_CONTEXT_CHANGED') setReady(false);
    if ([401, 403, 404].includes(cause?.response?.status) || cause?.code === 'AI_CLIENT_CONTEXT_CHANGED') clearPrivateData();
  }
  async function loadRecent(append = false) {
    if (!alive.current || recentLock.current) return;
    recentLock.current = true; setLoadingRecent(true); setRecentError('');
    const pending = request(), before = append ? recentCursor : null, privacyEpoch = privacy.current;
    try {
      const data = await api.list({ before, signal: pending.signal });
      if (!alive.current || pending.signal.aborted || privacyEpoch !== privacy.current) return;
      setRecent((old) => {
        const values = append ? [...old, ...data.items] : data.items;
        return [...new Map(values.map((item) => [item.conversation_uuid, item])).values()];
      });
      setRecentCursor(data.next_cursor);
    } catch (cause) {
      if (!alive.current || pending.signal.aborted || privacyEpoch !== privacy.current) return;
      denyIfNeeded(cause); setRecentError(assistantError(cause));
    } finally {
      pending.done(); recentLock.current = false;
      if (alive.current) setLoadingRecent(false);
    }
  }
  async function connect() {
    if (!alive.current) return;
    setConnecting(true); setError('');
    const pending = request(), privacyEpoch = privacy.current;
    try {
      await api.capabilities(pending.signal);
      if (!alive.current || pending.signal.aborted || privacyEpoch !== privacy.current) return;
      setReady(true); void loadRecent();
    } catch (cause) {
      if (!alive.current || pending.signal.aborted || privacyEpoch !== privacy.current) return;
      setReady(false); denyIfNeeded(cause);
      setError([401, 403].includes(cause?.response?.status) || cause?.code === 'AI_CLIENT_CONTEXT_CHANGED' ? assistantError(cause) :
        'The updated assistant is unavailable. Try connecting again after the backend is ready.');
    } finally { pending.done(); if (alive.current) setConnecting(false); }
  }
  useEffect(() => {
    alive.current = true; void connect();
    return () => { alive.current = false; view.current++; privacy.current++; controllers.current.forEach((item) => item.abort()); controllers.current.clear(); };
    // The parent remounts the workspace when account, unit or permissions change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, busy]);

  function newConversation() {
    if (lock.current) return;
    view.current++; setConversation(null); setMessages([]); setQuestion(''); setOlder(null);
    setError(''); setUncertain(false); setConversationLoaded(true); setRatings({});
  }
  async function openConversation(target, append = false) {
    if (!ready || lock.current) return;
    lock.current = true; setBusy(append ? 'older' : 'history'); setError('');
    if (!append) {
      view.current++; setMessages([]); setRatings({}); setOlder(null);
      setConversation({ conversation_uuid: target }); setConversationLoaded(false); setQuestion('');
    }
    const epoch = view.current, pending = request();
    try {
      const data = await api.conversation(target, { before: append ? older : null, signal: pending.signal });
      if (!valid(epoch) || pending.signal.aborted) return;
      setConversation(data.conversation);
      setMessages((old) => {
        const values = append ? [...data.messages, ...old] : data.messages;
        return [...new Map(values.map((item) => [item.message_uuid, item])).values()];
      });
      setOlder(data.next_cursor); setUncertain(false); setConversationLoaded(true);
    } catch (cause) {
      if (!valid(epoch) || pending.signal.aborted) return;
      denyIfNeeded(cause); setError(assistantError(cause));
    } finally {
      pending.done(); lock.current = false; if (alive.current) setBusy(null);
    }
  }
  async function askQuestion(text) {
    const clean = String(text ?? question).trim();
    if (!clean || Array.from(clean).length > 2000 || !ready || lock.current || uncertain ||
        !conversationLoaded || conversation?.status === 'archived') return;
    lock.current = true; setBusy('asking'); setError(''); setQuestion('');
    const epoch = view.current, pending = request(), temporary = 'pending-' + (++serial.current);
    setMessages((old) => [...old, { message_uuid: temporary, role: 'user', content: clean, source_type: 'none', pending: true }]);
    try {
      const reply = await api.ask(clean, { conversationUuid: conversation?.conversation_uuid, signal: pending.signal });
      if (!valid(epoch) || pending.signal.aborted) return;
      setConversation((old) => ({ ...old, conversation_uuid: reply.conversation_uuid, status: 'active' }));
      setMessages((old) => [...old.map((message) => message.message_uuid === temporary ?
        { ...message, message_uuid: reply.user_message_uuid, pending: false } : message),
      { message_uuid: reply.assistant_message_uuid, role: 'assistant', content: reply.answer,
        source_type: reply.source_type, kind: reply.kind, suggestions: reply.suggestions,
        advisory_only: true, created_at: reply.generated_at }]);
      void loadRecent();
    } catch (cause) {
      if (!valid(epoch) || pending.signal.aborted) return;
      setMessages((old) => old.map((message) => message.message_uuid === temporary ? { ...message, pending: false, failed: true } : message));
      setUncertain(assistantOutcomeUncertain(cause) || cause?.response?.status === 409); setQuestion(clean);
      denyIfNeeded(cause); setError(assistantError(cause)); void loadRecent();
    } finally { pending.done(); lock.current = false; if (alive.current) setBusy(null); }
  }
  async function rate(messageUuid, rating) {
    if (!ready || lock.current) return;
    lock.current = true; setBusy('feedback'); setError('');
    const epoch = view.current, pending = request();
    try {
      await api.feedback(messageUuid, rating, pending.signal);
      if (valid(epoch) && !pending.signal.aborted) setRatings((old) => ({ ...old, [messageUuid]: rating }));
    } catch (cause) {
      if (!valid(epoch) || pending.signal.aborted) return;
      denyIfNeeded(cause); setError(assistantError(cause));
    } finally { pending.done(); lock.current = false; if (alive.current) setBusy(null); }
  }
  const canAsk = ready && !busy && !uncertain && conversationLoaded && conversation?.status !== 'archived';
  const characters = Array.from(question).length;
  const suggestions = messages.slice().reverse().find((message) => message.role === 'assistant' && message.suggestions)?.suggestions || PROCUREMENT_QUESTIONS;

  return <div className="min-h-screen bg-slate-950 text-white px-4 md:px-8 py-8">
    <div className="max-w-7xl mx-auto space-y-6">
      <header className="rounded-3xl border border-cyan-700/40 bg-gradient-to-r from-slate-950 via-slate-900 to-cyan-950/40 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div><p className="text-xs uppercase tracking-[0.3em] text-cyan-300">SOLO.MDSH</p>
            <h1 className="text-3xl md:text-4xl font-bold mt-2">Procurement Assistant</h1>
            <p className="text-slate-400 mt-3 max-w-3xl">Review overdue orders, deliveries, rejections, backorders and invoice exceptions within your permitted company, unit and cost centres.</p>
          </div>
          <button type="button" onClick={newConversation} disabled={Boolean(busy)}
            className="rounded-xl border border-cyan-700 px-4 py-2 text-sm disabled:opacity-50">New conversation</button>
        </div>
      </header>
      {error && <div role="alert" className="rounded-xl border border-amber-700 bg-amber-950/30 p-4 text-sm text-amber-100">
        <p>{error}</p><div className="flex flex-wrap gap-3 mt-3">
          {!ready && <button type="button" onClick={connect} disabled={connecting} className="underline">Try connecting again</button>}
          {ready && conversation?.conversation_uuid && <button type="button" onClick={() => openConversation(conversation.conversation_uuid)} disabled={Boolean(busy)} className="underline">Reload saved conversation</button>}
          {ready && <button type="button" onClick={() => loadRecent()} disabled={loadingRecent} className="underline">Refresh saved conversations</button>}
        </div>
      </div>}
      <main className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_300px] gap-6">
        <section className="min-w-0 rounded-3xl border border-slate-800 bg-slate-900/70 overflow-hidden">
          <div className="border-b border-slate-800 p-4 flex items-center justify-between gap-3">
            <h2 className="font-semibold text-cyan-100">{conversation ? 'Saved conversation' : 'Ask a procurement question'}</h2>
            <span className="text-xs text-slate-400">{connecting ? 'Connecting…' : ready ? 'Connected' : 'Unavailable'}</span>
          </div>
          <div className="h-[560px] overflow-y-auto p-4 space-y-4" aria-label="Conversation" aria-busy={Boolean(busy)}>
            {older && <button type="button" disabled={Boolean(busy)} onClick={() => openConversation(conversation.conversation_uuid, true)}
              className="text-sm text-cyan-300 underline">Load older messages</button>}
            {!messages.length && <div className="rounded-2xl bg-slate-950/50 p-5 text-sm text-slate-400">
              {busy === 'history' ? 'Loading saved conversation…' : 'Choose a question below, or name a supplier, PO or cost centre. Include any delivery date period in your question.'}
            </div>}
            {messages.map((message) => <ChatBubble key={message.message_uuid} message={message}
              rating={ratings[message.message_uuid]} onRate={rate} disabled={Boolean(busy) || !ready} />)}
            {busy === 'asking' && <p role="status" className="text-sm text-slate-400">Checking your permitted procurement records…</p>}
            <div ref={bottomRef} />
          </div>
          <div className="border-t border-slate-800 p-4 space-y-3">
            {conversation?.status === 'archived' && <p className="text-sm text-amber-200">This conversation is archived. Start a new conversation to ask another question.</p>}
            <div className="flex flex-wrap gap-2">{suggestions.map((item) => <button key={item} type="button" onClick={() => askQuestion(item)} disabled={!canAsk}
              className="rounded-full border border-cyan-700/50 bg-cyan-950/30 px-3 py-1.5 text-xs text-cyan-100 hover:bg-cyan-900/50 disabled:opacity-50">{item}</button>)}</div>
            <form onSubmit={(event) => { event.preventDefault(); void askQuestion(); }} className="flex gap-2">
              <input aria-label="Procurement question" value={question} onChange={(event) => setQuestion(event.target.value)} maxLength={4000}
                disabled={!canAsk} placeholder="For example: Show backorders for supplier “Exact name”"
                className="min-w-0 flex-1 rounded-xl bg-slate-950 border border-slate-700 px-4 py-3 text-sm focus:ring-2 focus:ring-cyan-500 disabled:opacity-50" />
              <button type="submit" disabled={!canAsk || !question.trim() || characters > 2000}
                className="rounded-xl bg-emerald-600 px-5 py-3 text-sm font-bold hover:bg-emerald-500 disabled:opacity-50">Ask</button>
            </form>
            <p className={'text-xs ' + (characters > 2000 ? 'text-red-300' : 'text-slate-500')}>{characters.toLocaleString()} / 2,000 characters · Advice only; approvals, payments and receiving use their normal screens.</p>
          </div>
        </section>
        <aside className="space-y-4">
          <section className="rounded-3xl border border-slate-800 bg-slate-900/70 p-4">
            <div className="flex items-center justify-between gap-2"><h2 className="font-semibold">Saved conversations</h2>
              <button type="button" onClick={() => loadRecent()} disabled={!ready || loadingRecent || Boolean(busy)} className="text-xs text-cyan-300 underline">Refresh</button></div>
            {recentError && <p role="alert" className="text-sm text-amber-200 mt-3">{recentError}</p>}
            {!recent.length && <p className="text-sm text-slate-500 mt-3">{loadingRecent ? 'Loading…' : recentCursor ? 'No accessible conversations on this page.' : 'No saved conversations to display.'}</p>}
            <div className="mt-3 space-y-2">{recent.map((item) => <button key={item.conversation_uuid} type="button" onClick={() => openConversation(item.conversation_uuid)}
              disabled={Boolean(busy)} className={'w-full rounded-xl border px-3 py-3 text-left text-sm disabled:opacity-50 ' +
                (conversation?.conversation_uuid === item.conversation_uuid ? 'border-cyan-700 bg-cyan-950/30' : 'border-slate-800 bg-slate-950/50 hover:border-slate-600')}>
              <span className="block break-words">{item.title || 'Saved conversation'}</span>
              <span className="text-xs text-slate-500">{item.status === 'archived' ? 'Archived · ' : ''}{new Date(item.updated_at).toLocaleString()}</span>
            </button>)}</div>
            {recentCursor && <button type="button" onClick={() => loadRecent(true)} disabled={loadingRecent || Boolean(busy)} className="mt-3 text-sm text-cyan-300 underline">Load more conversations</button>}
          </section>
          <section className="rounded-3xl border border-slate-800 bg-slate-900/70 p-4 text-sm text-slate-400 space-y-3">
            <h2 className="font-semibold text-white">Ask precisely</h2>
            <p>Use an exact supplier name, PO reference or cost-centre code.</p>
            <p>Delivery questions accept dates such as “next week”. Rejection history covers all recorded dates; backorders and invoice review use current balances.</p>
            <p>Totals describe all matching records. The answer labels any shorter list of examples.</p>
          </section>
        </aside>
      </main>
    </div>
  </div>;
}
function ChatBubble({ message, rating, onRate, disabled }) {
  const isUser = message.role === 'user';
  return <div className={'flex ' + (isUser ? 'justify-end' : 'justify-start')}>
    <article className={'min-w-0 rounded-2xl px-4 py-3 text-sm ' + (isUser ?
      'max-w-[85%] bg-cyan-700 text-white' : 'w-full border border-slate-700 bg-slate-950/80 text-slate-100')}>
      {isUser ? <p className="whitespace-pre-wrap break-words">{message.content}</p> : <>
        <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">{message.source_type === 'live_system' ? 'Live procurement records' : message.kind === 'clarify' ? 'Clarification' : 'Clarification or guidance'}</p>
        <ProcurementAnswer content={message.content} />
        <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-slate-400">
          <button type="button" aria-pressed={rating === 1} onClick={() => onRate(message.message_uuid, 1)} disabled={disabled}
            className={rating === 1 ? 'text-emerald-300' : 'hover:text-white disabled:opacity-50'}>Helpful</button>
          <button type="button" aria-pressed={rating === -1} onClick={() => onRate(message.message_uuid, -1)} disabled={disabled}
            className={rating === -1 ? 'text-amber-300' : 'hover:text-white disabled:opacity-50'}>Not helpful</button>
          {rating && <span>Feedback saved</span>}
        </div>
      </>}
      {message.failed && <p className="text-xs mt-2 text-amber-200">This request was not confirmed.</p>}
    </article>
  </div>;
}
